import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tyndo/ui-kit';
import { AuthShell, AuthError } from './AuthShell';
import { verify, storeSessionToken } from '../auth/nativeAuth';
import { recordError } from '../observability/telemetry';

type VerifyState = 'verifying' | 'success' | 'error';

/**
 * Email-verification return leg ('/auth/verify?token=…'). Reads the one-shot
 * token from the query string, POSTs it to /api/v1/auth/verify, and on success
 * stores the issued session token + navigates the user straight in.
 */
export default function VerifyPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('verifying');
  const [error, setError] = useState<string | null>(null);
  // Guard against React StrictMode double-invoke consuming the one-shot token twice.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setState('error');
      setError('This verification link is missing its token. Please use the link from your email.');
      return;
    }

    const run = async () => {
      try {
        const session = await verify(token);
        storeSessionToken(session.token);
        setState('success');
        // Brief beat so the "welcome" copy is seen before redirecting in.
        window.setTimeout(() => navigate('/'), 1200);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'We could not verify your email.';
        setState('error');
        setError(message);
        recordError('auth.verify_failed', err, {});
      }
    };

    void run();
  }, [navigate]);

  if (state === 'verifying') {
    return (
      <AuthShell title="Verifying your email…">
        <div className="flex flex-col items-center gap-4 py-2">
          <div
            role="status"
            aria-label="Verifying"
            className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary"
          >
            <span className="sr-only">Verifying…</span>
          </div>
          <p className="text-muted-foreground text-sm">Just a moment.</p>
        </div>
      </AuthShell>
    );
  }

  if (state === 'success') {
    return (
      <AuthShell title="Email verified — welcome">
        <p className="text-sm text-muted-foreground text-center">
          Taking you to your workspace…
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Verification failed">
      <div className="space-y-4">
        {error && <AuthError message={error} />}
        <p className="text-sm text-muted-foreground">
          The link may have expired or already been used. You can request a new
          verification email from the sign-in page.
        </p>
        <Button onClick={() => navigate('/login')} className="w-full">
          Back to sign in
        </Button>
      </div>
    </AuthShell>
  );
}
